import { useIntl, FormattedMessage } from "react-intl";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { cancelManualQuote } from "api";
import { quoteRequestQueryKeys } from "api/queries/quotes/quoteRequestQueryKeys";
import Dialog from "components/organisms/Dialog/Dialog";
import Button from "components/v2/Button/Button";
import errorToast from "helpers/errorToast";
import { shipmentQueryKeys } from "api/queries/shipments/shipmentQueryKeys";

interface CancelManualParcelQuoteDialogProps {
  isOpen: boolean;
  close: () => void;
  quotation: any;
}

function CancelManualParcelQuoteDialog({ isOpen, close, quotation }: CancelManualParcelQuoteDialogProps) {
  const { id } = useParams();
  const intl = useIntl();
  const queryClient = useQueryClient();

  const refetchQuotationLists = () => {
    queryClient.invalidateQueries({ queryKey: quoteRequestQueryKeys.quoteQuotationListing(id!) });
    queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentQuotationActionHistoryDetails(id!) });
  };

  const { mutate, isLoading } = useMutation({
    mutationFn: () => cancelManualQuote(quotation.reference),
    onSuccess: (res) => {
      if (res.status === 200) {
        toast("Quotation cancelled", { type: "success" });
        refetchQuotationLists();
      } else {
        throw new Error("Could not cancel quote. Please try again later.");
      }
    },
    onError: (err) => {
      errorToast(err, "Could not cancel quote. Please try again later.");
    },
  });

  const handleCancelManualQuote = () => {
    mutate();
  };

  return (
    <Dialog
      size="sm"
      title={intl.formatMessage({
        id: "shipment-quotes.components.quotes.cancelManualQuote",
        defaultMessage: "Cancel manual quote",
      })}
      show={isOpen}
      onHide={close}
    >
      <p className="">
        <FormattedMessage
          id="shipment-quotes.components.quotes.cancelManualQuote.confirmationMessage"
          defaultMessage="Are you sure you want to cancel this manual quote?"
        />
      </p>

      <div className="d-flex justify-content-end gap-lg mt-lg">
        <Button
          label={intl.formatMessage({
            id: "general.cancel",
            defaultMessage: "Cancel",
          })}
          onClick={close}
          variant="secondary-color"
          disabled={isLoading}
        />

        <Button
          label={intl.formatMessage({
            id: "general.confirm",
            defaultMessage: "Confirm",
          })}
          onClick={handleCancelManualQuote}
          variant="error"
          disabled={isLoading}
        />
      </div>
    </Dialog>
  );
}

export default CancelManualParcelQuoteDialog;
